import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Box, Stack, Typography } from '@mui/material';

import Checkbox from '../checkbox/Checkbox';
import CheckboxTree from 'react-checkbox-tree';
import Icon from '../Icon';
import Search from './Search';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';

import {
  treeUpdated,
  updateSelectedGroup,
  undoSelectedGroup,
  resetSelectedGroups,
  parseUserIdsForBackend,
  undoUsersOfSelectedGroup,
  selectAllUsersOfSelectedGroup,
  updateSelectedGroups,
  undoUsers,
  createArrayFromSelectedGroups,
} from '../../store/treeReducer';
import { reducedUsersToTheirInitialSize } from '../../store/usersReducer';
import {
  createUsersMap,
  createGroupTree,
  createTree,
} from '../../utils/makeTree';

import './Collections.scss';

import {
  useGetAuthenticationQuery,
  useGetDomainsQuery,
  useGetGroupsQuery,
  useGetUsersWithProfilesQuery,
} from '../../services/ADapi';

const treeIcons = {
  check: <Icon id="check" width="20" height="20" />,
  halfCheck: <Icon id="halfcheck" width="20" height="20" />,
  uncheck: <Icon id="incheck" width="20" height="20" />,
  expandClose: <Icon id="collapse" width="14" height="14" />,
  expandOpen: <Icon id="expand" width="14" height="14" />,
};

const CollectionsConfigurate = ({ collectionName, checkedUsers }) => {
  const dispatch = useDispatch();
  const tree = useSelector((state) => state.tree.tree);
  const selectedGroups = useSelector((state) => state.tree.selectedGroups);

  const arrayOfSelectedItems = useSelector(
    (state) => state.tree.arrayOfSelectedItems
  );
  const filteredUsersFromApi = useSelector(
    (state) => state.users.filteredUsersFromApi
  );

  const {
    data: { user_id },
  } = useGetAuthenticationQuery();

  const { data: USERS } = useGetUsersWithProfilesQuery(user_id);
  const { data: DOMAINS } = useGetDomainsQuery(user_id);
  const { data: GROUPS, isLoading: isGroupsLoading } =
    useGetGroupsQuery(user_id);

  const [expanded, setExpanded] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [nodesFiltered, setNodesFiltered] = useState([]);
  const [breadcrumbs, setIsBreadcrumbs] = useState('');
  const [rawGroupId, setRawGroupId] = useState(0);
  const [selectAllControlState, setSelectAllControlState] = useState(false);
  const [lastChildren, setLastChildren] = useState([]);
  // const [groupFilterText, setGroupFilterText] = useState('');

  // This will roll back users array to its initial size
  // otherewise there'll be an error with duplicating ids
  useEffect(() => {
    dispatch(reducedUsersToTheirInitialSize());

    // Reset selected users when leave this component
    return () => {
      dispatch(resetSelectedGroups());
    };
  }, [dispatch]);

  useEffect(() => {
    const users =
      filteredUsersFromApi.length > 0 ? filteredUsersFromApi : USERS;

    const usersMap = createUsersMap(users || [] /* users can be undefined */);
    const groupsTree = createGroupTree(GROUPS || [], null, usersMap);
    const tree = createTree(DOMAINS || [], groupsTree);

    dispatch(treeUpdated(tree));
  }, [USERS, GROUPS, DOMAINS, filteredUsersFromApi, dispatch]);

  // This effect renders previously selected users in created collections
  useEffect(() => {
    if (checkedUsers && !isGroupsLoading) {
      GROUPS.forEach((group) => {
        // checking for a match of selected users in the tree
        const filterSelected = group.ad_users.filter((user) =>
          checkedUsers.includes(user)
        );

        filterSelected.forEach((userId) => {
          const key = `d${group.domain}-g${group.id}-u${userId}`;

          dispatch(updateSelectedGroups(key));
        });
      });
    }
  }, [GROUPS, isGroupsLoading, checkedUsers, tree, dispatch]);

  useEffect(() => {
    setNodesFiltered(tree);
  }, [tree, dispatch]);

  useEffect(() => {
    dispatch(createArrayFromSelectedGroups());
    dispatch(parseUserIdsForBackend());
  }, [selectedGroups, dispatch]);

  useEffect(() => {
    setSelectAllControlState(false);

    if (rawGroupId && selectedGroups[rawGroupId]) {
      const groupSize = Object.values(selectedGroups[rawGroupId]).length;

      if (groupSize >= lastChildren.length) {
        setSelectAllControlState(true);
      } else {
        setSelectAllControlState(false);
      }
    }
  }, [rawGroupId, selectedGroups, lastChildren, setSelectAllControlState]);

  const filterNodes = (filtered, node) => {
    const children = (node.children || []).reduce(filterNodes, []);

    if (
      node.label.toLocaleLowerCase().indexOf(filterText.toLocaleLowerCase()) >
        -1 ||
      children.length
    ) {
      filtered.push({ ...node, ...(children.length && { children }) });
    }

    return filtered;
  };

  const filterTree = () => {
    if (!filterText) {
      setNodesFiltered(tree);

      return;
    }

    setNodesFiltered(tree.reduce(filterNodes, []));
  };

  const onFilterHandler = (e) => {
    setFilterText(e.target.value);

    e.target.value ? filterTree() : setNodesFiltered(tree);
  };

  // const onGroupFilterHandler = (e) => {
  //   setGroupFilterText(e.target.value);
  // };

  const onCheckHandler = (event, itemId) => {
    const checked = event.target.checked;

    if (itemId.includes('groupId')) {
      if (checked) {
        dispatch(updateSelectedGroup(itemId));
      } else {
        dispatch(undoSelectedGroup(itemId));
      }

      return;
    }

    if (checked) {
      dispatch(updateSelectedGroups(itemId));
    } else {
      dispatch(undoUsers(itemId));
    }
  };

  const selectAllHandler = (e) => {
    const isChecked = e.target.checked;

    setSelectAllControlState(isChecked);

    if (isChecked) {
      // This might be useful in future. Don't delete it
      // if (lastChildren.length > 40) {
      //   dispatch(fetchAllUsersOfSelectedGroup(user_id, rawGroupId));
      // } else {
      //   dispatch(selectAllUsersOfSelectedGroup(rawGroupId));
      // }
      dispatch(selectAllUsersOfSelectedGroup(rawGroupId));
    } else {
      dispatch(undoUsersOfSelectedGroup(rawGroupId));
    }
  };

  return (
    <Box sx={{ pt: 7 }}>
      <Typography variant="h3" sx={{ pb: 2 }}>
        Users
      </Typography>
      <div className="collections-form-configurate">
        <div className="collections-form-configurate-control">
          <div className="collections-form-configurate-control-breadcrumbs">
            {collectionName ? collectionName : 'Collection name'} /{' '}
            {breadcrumbs}
          </div>
        </div>

        <Grid container sx={{ minHeight: 400 }}>
          <Grid item xs={6} sx={{ p: 2, borderRight: '1px solid #e4edfe' }}>
            <Search
              value={filterText}
              userId={user_id}
              groupId={rawGroupId}
              placeholder="Search in tree"
              onChange={onFilterHandler}
              sx={{ mb: 2 }}
            />

            <CheckboxTree
              nodes={nodesFiltered}
              checked={arrayOfSelectedItems}
              expanded={expanded}
              onExpand={setExpanded}
              showNodeIcon={false}
              expandOnClick
              disabled
              onClick={(expanded) => {
                // Firstly reset group id. It's needed to separate same logic between groups
                // For example, Select all control relies on that, switching between groups should disable Select all control
                setRawGroupId(0);
                // Store a raw number of group id. Only a number 6 will be saved from {groupId: 'd1-g6'}
                const groupId = expanded.value.split('-groupId')[1];
                setRawGroupId(groupId);

                setIsBreadcrumbs(
                  expanded.parent.title !== undefined
                    ? expanded.parent.title
                    : ''
                );

                setFilterText(expanded.label);

                if (expanded.children) {
                  setLastChildren(expanded.children);
                }
              }}
              icons={treeIcons}
            />
          </Grid>
          <Grid item xs={6} sx={{ p: 2 }}>
            <Stack>
              {/* <Search
                value={groupFilterText}
                userId={auth.user_id}
                groupId={rawGroupId}
                placeholder="Search in group"
                onChange={onGroupFilterHandler}
                sx={{ mb: 2 }}
              /> */}
              <Checkbox
                disabled={!rawGroupId > 0}
                label="Select all"
                checked={selectAllControlState}
                onChange={selectAllHandler}
              />
              {lastChildren.length ? (
                <>
                  {lastChildren.map((child) => {
                    const isUserNode = child.hasOwnProperty('last_seen');
                    const icon = isUserNode ? (
                      <Icon id="user" width="24" height="24" />
                    ) : (
                      <Icon id="group" width="24" height="24" />
                    );

                    let isGroupSelected = false;

                    // Here make sure that current child is a group with children
                    if (child.children && child.children.length > 0) {
                      // Run check to make sure that every item from group's children has 'checked' state
                      // which means that group should be checked
                      const groupId = child.value.split('-groupId')[1];

                      if (groupId && selectedGroups[groupId]) {
                        const groupSize = Object.values(
                          selectedGroups[groupId]
                        ).length;

                        // Since we show 50 users per page as default, actual group size can be more than 50 users
                        isGroupSelected = groupSize >= child.children.length;
                      }
                    }

                    return (
                      <Box key={child.value} sx={{ pt: 1, pb: 1 }}>
                        <Checkbox
                          label={
                            <>
                              {icon}
                              {child.label}
                            </>
                          }
                          checked={
                            isGroupSelected
                              ? isGroupSelected
                              : arrayOfSelectedItems.includes(child.value)
                          }
                          onChange={(event) =>
                            onCheckHandler(event, child.value)
                          }
                        />
                      </Box>
                    );
                  })}
                </>
              ) : null}
            </Stack>
          </Grid>
        </Grid>
      </div>
    </Box>
  );
};

export default CollectionsConfigurate;
